import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Spinner,
} from 'react-bootstrap';
import { ArrowBack, CloudUpload, Download as DownloadIcon } from '@mui/icons-material';
import CommonHeader from 'layouts/dashboard/CommonHeader';
import { useToast } from 'context/toast-context/ToastProvider';
import UploadFile from 'components/courses/add-new-course/steps/UploadFile';
import courseIcon from '../../assets/images/Courses.svg';
import { notesExtractor, DownloadPDFAPI } from 'ServerAPI/serverAPI';
import RichTextEditor from 'components/richTextEditor/richTextEditor';
import RenderLatex from 'components/content-generation/RenderLatex.jsx';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

const sanitizeFileName = (name) => {
  const baseName = name.replace(/\.[^/.]+$/, '');
  const cleanBaseName = baseName.replace(/[^a-zA-Z0-9_-]/g, '_');
  return cleanBaseName + '.pdf' || 'untitled.pdf';
};

const NotesUpload = () => {
  const [cookies] = useCookies(['userId', 'organizationId']);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [title, setTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // This will hold the editable HTML (with working LaTeX)
  const [editorContent, setEditorContent] = useState('');

  // Auto-fill title
  useEffect(() => {
    if (selectedFile) {
      setTitle(sanitizeFileName(selectedFile.name));
    } else {
      setTitle('');
    }
  }, [selectedFile]);

  // When extraction completes → convert to editable HTML with LaTeX
useEffect(() => {
  if (!extractedData?.extracted_text) {
    setEditorContent('');
    return;
  }

  const pages = extractedData.extracted_text;
  const pageNumbers = Object.keys(pages).sort((a, b) => Number(a) - Number(b));

  let fullText = '';

  pageNumbers.forEach((pageNum, pageIndex) => {
    const text = pages[pageNum] || '';
    
    // Har \n ko <br><br> bana do → Quill isko properly respect karta hai
    const lines = text.split('\n');

    lines.forEach((line, i) => {
      if (line.trim() === '') {
        fullText += '<br>';  // blank line
      } else {
        fullText += line.trimEnd();
        fullText += '<br><br>';  // har line ke baad proper gap
      }
    });

    // Page ke beech extra spacing
    if (pageIndex < pageNumbers.length - 1) {
      fullText += '<br><br>';
    }
  });

  // Yeh HTML string Quill ko samajh aata hai
  setEditorContent(fullText);
}, [extractedData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setUploadError(null);
    setSelectedFile(null);
    setExtractedData(null);
    setEditorContent('');

    if (!file) return;
    if (file.type !== 'application/pdf') {
      setUploadError('Only PDF files are allowed');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File size must be 25 MB or less');
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const response = await notesExtractor(selectedFile);
      setExtractedData(response);
      showToast('Notes uploaded and extracted successfully!', 'success');
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Upload failed';
      setUploadError(msg);
      showToast(msg, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async () => {
    if (!editorContent.trim()) {
      showToast('Nothing to download', 'warning');
      return;
    }

    setIsDownloading(true);

    const payload = {
      userId: cookies.userId,
      organizationId: cookies.organizationId,
      title: title || 'Digitized Notes',
      fileName: selectedFile?.name || null,
      pages: [{
        pageNumber: 1,
        contentHtml: editorContent, // This has LaTeX + formatting
        rawText: editorContent.replace(/\$/g, ''), // fallback
      }]
    };

    try {
      const response = await DownloadPDFAPI(payload);
      if (!(response?.data instanceof Blob)) {
        showToast('Failed to generate PDF', 'error');
        return;
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title || 'digitized-notes'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast('PDF downloaded successfully', 'success');
    } catch (err) {
      showToast('Download failed', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleBack = () => navigate('/content-generation-dashboard');

  return (
    <div className="container-fluid">
      <Row>
        <Col>
          <CommonHeader
            text="Digitize Notes"
            subText="Upload handwritten or scanned notes as PDF (max 25 MB)."
            icon={courseIcon}
          />
        </Col>
      </Row>

      <Row className="mx-auto">
        <Col sm={10} md={8} className="mx-auto pt-5 px-0">
          <Card>
            <Card.Body className="p-4">

              {/* Title */}
              <div className="mb-4">
                <Form.Label className="fw-semibold">Notes Title</Form.Label>
                <Form.Control type="text" value={title} readOnly disabled className="bg-light" />
              </div>

              {/* Upload */}
              <div className="mb-4">
                <Form.Label className="fw-semibold">Upload Notes (PDF) *</Form.Label>
                <UploadFile
                  fileType="pdf"
                  handleFileChange={handleFileChange}
                  showFileInfo
                  selectedFile={selectedFile}
                  disabled={isUploading}
                  accept=".pdf"
                />
              </div>

              {uploadError && <Alert variant="danger">{uploadError}</Alert>}

              <div className="d-flex gap-3 mb-4">
                <Button variant="secondary" onClick={handleBack} disabled={isUploading}>
                  <ArrowBack fontSize="small" className="me-1" /> Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleUpload}
                  disabled={isUploading || !selectedFile}
                  className="flex-grow-1"
                >
                  {isUploading ? (
                    <> <Spinner size="sm" className="me-2" /> Uploading... </>
                  ) : (
                    <> <CloudUpload fontSize="small" className="me-1" /> Upload & Extract Notes </>
                  )}
                </Button>
              </div>

              {/* Extracted + Editable Content */}
              {editorContent && (
                <div className="mt-5">
                  <h5 className="fw-semibold mb-3">Edit Your Extracted Notes</h5>
                  <Card className="shadow-sm">
                    <Card.Body className="p-0">
<RichTextEditor
  initialContent={editorContent}
  onChange={setEditorContent}
  preserveWhitespace={true}           // Yeh chahiye
  editorProps={{
    attributes: {
      class: 'preserve-line-breaks',  // Yeh class apply hogi
    },
  }}
/>
                    </Card.Body>
                  </Card>

                  <div className="d-flex justify-content-end mt-3">
                    <Button variant="success" onClick={handleDownload} disabled={isDownloading}>
                      {isDownloading ? (
                        <> <Spinner size="sm" className="me-2" /> Preparing PDF... </>
                      ) : (
                        <> <DownloadIcon fontSize="small" className="me-2" /> Download Edited PDF </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default NotesUpload;